import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useHasMounted from 'hooks/useHasMounted'
import { useCallback, useContext, useEffect, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { canBookNow, getCalendarMonths, getDaysOtherAvailability, getPackagesForFilters, getSelectedOfferPageDay, getSelectedPackageView, isFetchingCalendars } from 'selectors/offerPage/offerPageSelectors'
import OfferCalendarDayPicker from './DayPicker/OfferCalendarDayPicker'
import OfferCalendarMonthPicker from './MonthPicker/OfferCalendarMonthPicker'
import OfferCalendarNoFlightsMessage from './OfferCalendarNoFlightsMessage'
import OfferCalendarNotAvailableMessage from './OfferCalendarNotAvailableMessage'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownSheetBody from 'LuxKit/Dropdown/Sheet/DropdownSheetBody'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import ModalProvider from 'contexts/ModalProvider'
import { EmptyArray } from 'lib/array/arrayUtils'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import {
  getMultiRoomCalendarMonths,
  getMultiRoomDaysOtherAvailability,
  getMultiRoomPackagesForFilters,
  getSelectedOfferPageMultiRoomDay,
  isMultiRoomFetchingCalendars,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import OfferCalendarDatePickerDropdownHeader from './OfferCalendarDatePickerDropdownHeader'
import { CalendarBurnProvider } from './CalendarBurnContext'
import useCalendarBurnToggleVisible from './useCalendarBurnToggleVisible'
import OfferCalendarDatePickerDropdownFooter from './OfferCalendarDatePickerDropdownFooter'
import CalendarPickerRecommendations from './Recommendations/CalendarPickerRecommendations'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import OfferCalendarFlightDealBanner from './OfferCalendarFlightDealBanner'
import { shouldShowFlightDealBanner } from 'agentHub/selectors/agentHubSelectors'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import useFixedFlashLengthOfStayExperiment from './useFixedFlashLengthOfStayExperiment'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'

interface Props {
  offer: App.Offer
  checkIn?: string
  fetching?: boolean
  selectedDay?: App.CalendarDay
  flights?: boolean
  calendarError?: boolean
  calendarAvailability: boolean
  useRooms?: boolean
  defaultDayPicker?: boolean
  defaultMonth?: string
  duration: number
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView?: App.OfferPackageOptionView
  recommendationsShown: boolean
  rooms?: Array<App.Occupants>
  otherAvailability: { [checkIn: string]: boolean }
  isAnyFlightsActive: boolean
  subFilters?: Array<OfferPageFilterType>
  scrollRef: React.RefObject<HTMLDivElement | null>
  canBookNow?: boolean
  airport?: App.Airport
  shouldActivateFixedFlashLengthOfStayExperiment: boolean
}

const DEFAULT_SUB_FILTERS: Array<OfferPageFilterType> = ['duration', 'airport', 'room']
const READ_EXPERIMENT_WITHOUT_ACTIVATION = { canActivate: false }

function OfferCalendarDatePicker(props: Props) {
  const {
    checkIn,
    offer,
    fetching,
    selectedDay,
    flights,
    calendarError,
    calendarAvailability,
    defaultMonth,
    defaultDayPicker,
    duration,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    rooms = EmptyArray,
    recommendationsShown,
    isAnyFlightsActive,
    subFilters = DEFAULT_SUB_FILTERS,
    scrollRef,
    canBookNow,
    airport,
    shouldActivateFixedFlashLengthOfStayExperiment,
  } = props

  const intl = useIntl()
  const isBurnToggleVisible = useCalendarBurnToggleVisible()
  const { isActive: isWeddingGiftRegistry } = useWeddingGiftRegistryContext()

  const shouldRenderFlightDealBanner = shouldShowFlightDealBanner(offer)
  const { isAnyVariant: isNewFlightUpsellVariant } = useExperiment(Experiments.croNewFlightUpsellTest, READ_EXPERIMENT_WITHOUT_ACTIVATION)
  const isFixedFlashLengthOfStayOffer = isFlashHotelOffer(offer)
  const isFixedFlashLengthOfStayVariant = useFixedFlashLengthOfStayExperiment(isFixedFlashLengthOfStayOffer && shouldActivateFixedFlashLengthOfStayExperiment)
  const isFixedFlashLengthOfStayEnabled = isFixedFlashLengthOfStayOffer && isFixedFlashLengthOfStayVariant
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const [monthIndex, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const mounted = useHasMounted()

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(selectedPackageView?.package)

  useEffect(() => {
    if (!fetching && checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [checkIn, selectedDay]) // oxlint-disable-line react-hooks/exhaustive-deps

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onBackClick = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }

    setMonth(undefined)
  }, [setMonth, dispatch, recommendationsShown])

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }
    dispatch({ type: OfferPageActions.resetDates })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [dispatch, onUserAnalyticsAction, recommendationsShown])

  // calendar reco on mobile is handled separately by OfferCalendarPickerRecommendationsModal
  const isMobile = useScreenSizeOnly('mobile')

  return (<ModalProvider>
    <CalendarBurnProvider>
      <OfferCalendarDatePickerDropdownHeader
        subFilters={subFilters}
        monthIndex={monthIndex}
        isCalendarBurnToggleVisible={isBurnToggleVisible}
        isNewFlightUpsellVariant={isNewFlightUpsellVariant}
        onBackClick={onBackClick}
        onDismissClick={onDropdownClose}
      />
      <DropdownSheetBody ref={scrollRef}>
        {recommendationsShown && !isMobile && (<CalendarPickerRecommendations
          offerId={offer.id}
          offerType={offer.type}
          rooms={rooms}
          heading={intl.formatMessage(
            {
              id: '2kg5LD4fnxQfhLWXEtQT',
              defaultMessage: 'Similar hotels to {hotelName}',
              description: 'Heading for similar hotel recommendations based on selected property',
            },
            { hotelName: offer.property?.name ?? offer.location },
          )}
          seedOffer={offer}
        />)}
        {!recommendationsShown && (
          <div style={isBurnToggleVisible ? { paddingTop: 12 } : undefined}>
            {mounted && !fetching && !calendarAvailability && <>
              {(calendarError || !flights) && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
              {!calendarError && flights && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
            </>}
            {calendarAvailability && <VerticalSpacer gap={24}>
              {!isNewFlightUpsellVariant && shouldRenderFlightDealBanner && <OfferCalendarFlightDealBanner
                destinationAirportCode={offer.flightDestinationPort}
                originAirportCode={airport?.code}
                isFlightsActive={isAnyFlightsActive}
              />}
              {isAnyFlightsActive && <Caption variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'l6wclXQC8CR1sd0sFcfo',
                    defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply',
                    description: 'Calendar pricing disclaimer for flights with brand terms and conditions',
                  },
                  { brandName: config.brandConfig.name },
                )}
              </Caption>}
              {monthIndex !== undefined && !fetching && <OfferCalendarDayPicker
                onBackToMonth={onBackToMonth}
                initialMonth={monthIndex}
                defaultDayPicker={defaultDayPicker}
                flightsOnly={offer.bundledWithFlightsOnly}
                isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
                contentRef={scrollRef}
              />}
              {(monthIndex === undefined || fetching) && <OfferCalendarMonthPicker
                fetching={fetching}
                onMonthSelect={setMonth}
              />}
            </VerticalSpacer>}
          </div>
        )}
      </DropdownSheetBody>
      <OfferCalendarDatePickerDropdownFooter
        isSelectedTargetUpgradePackage={!!selectedTargetUpgradePackage}
        canBook={isWeddingGiftRegistry ? false : canBookNow}
        offer={offer}
        selectedDay={selectedDay}
        duration={duration}
        relevantPackages={relevantPackages}
        selectedPackageView={selectedPackageView}
        isAnyFlightsActive={isAnyFlightsActive}
        isOnMonthPicker={monthIndex === undefined}
      />
    </CalendarBurnProvider>
  </ModalProvider>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    checkIn: state.checkInDate,
    offer: state.offer,
    flights: state.flights,
    duration: state.duration,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(state.offer),
    relevantPackages: isMultiRoom ? getMultiRoomPackagesForFilters(state, appState) : getPackagesForFilters(appState, state),
    selectedPackageView: getSelectedPackageView(state, appState),
    rooms: state.rooms,
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    fetching: isMultiRoom ? isMultiRoomFetchingCalendars(state, appState) : isFetchingCalendars(appState, state),
    calendarAvailability: isMultiRoom ? getMultiRoomCalendarMonths(state, appState).availableCount > 0 : getCalendarMonths(appState, state).availableCount > 0,
    calendarError: !!state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    isAnyFlightsActive: state.flights,
    canBookNow: canBookNow(state, appState),
    airport: state.airport,
    shouldActivateFixedFlashLengthOfStayExperiment: state.activeFilter === 'checkin' || state.activeHeaderFilter === 'checkin',
    open: !!state.activeFilter,
  }
}, OfferCalendarDatePicker, preventRenderWithClosedFilter)
